import { Edit3, Plus } from "lucide-react";
import { PermissionButton } from "@/components/ui/permission-button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { getTypeIcon } from "./typeIcons";
import { NeedsConfigPill } from "./NeedsConfigPill";
import { formatDateDisplay } from "@/lib/utils";
import { getPlanTypeAccent } from "@shared/schemas/planTypes";
import { getWaiveReasonLabel } from "@shared/utils/waiveReasons";

interface WaivedBenefitRowProps {
  benefit: any;
  onEditWaiver: () => void;
  onEnrollInstead: () => void;
  /** When true, render the amber "Needs configuration" pill (plan has no contribution rows). */
  needsConfig?: boolean;
  /** When true, hide the per-row actions (Edit waiver / Enroll instead). Employee /benefits view. */
  readOnly?: boolean;
}

function humanizeReason(eventType: string | null | undefined): string {
  if (!eventType) return "Waived";
  return getWaiveReasonLabel(eventType);
}

export function WaivedBenefitRow({
  benefit,
  onEditWaiver,
  onEnrollInstead,
  needsConfig,
  readOnly = false,
}: WaivedBenefitRowProps) {
  const plan = benefit?.plan;
  const planType = String(plan?.type || "");
  const Icon = getTypeIcon(planType);
  const reason = humanizeReason(benefit?.eventType);
  const accent = getPlanTypeAccent(planType);

  return (
    <div className={cn(
      "rounded-lg border border-dashed border-stone-300 dark:border-stone-700",
      "bg-stone-100 dark:bg-stone-900/60",
      "border-l-4 border-l-stone-400 dark:border-l-stone-500",
      "px-4 py-3 flex flex-wrap items-center gap-3",
    )}>
      <div className={cn("h-7 w-7 rounded-md flex items-center justify-center shrink-0 opacity-70", accent.accentBg)}>
        <Icon className={cn("h-4 w-4", accent.accentText)} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <Badge
            variant="outline"
            className="bg-stone-200 text-stone-800 border-stone-400 dark:bg-stone-700 dark:text-stone-100 dark:border-stone-500 font-semibold uppercase tracking-wide text-[10.5px]"
          >
            Waived
          </Badge>
          {needsConfig && <NeedsConfigPill />}
          {benefit?.effectiveDate && (
            <span className="text-[11px] text-stone-500 dark:text-stone-400 font-mono">
              eff. {formatDateDisplay(benefit.effectiveDate)}
            </span>
          )}
        </div>
        <div className="text-xs text-stone-600 dark:text-stone-400 mt-1">
          {reason}
          {plan?.name && (
            <span className="text-stone-400 dark:text-stone-500"> · {plan.name}</span>
          )}
        </div>
      </div>
      {!readOnly && (
        <div className="flex items-center gap-2 ml-auto">
          <PermissionButton
            permission="clients:edit"
            whenDenied="hide"
            variant="ghost"
            size="sm"
            className="gap-1.5 text-xs"
            onClick={onEditWaiver}
          >
            <Edit3 className="h-3.5 w-3.5" />
            Edit waiver
          </PermissionButton>
          <PermissionButton
            permission="clients:edit"
            whenDenied="hide"
            variant="ghost"
            size="sm"
            className="gap-1.5 text-xs"
            onClick={onEnrollInstead}
          >
            <Plus className="h-3.5 w-3.5" />
            Enroll instead
          </PermissionButton>
        </div>
      )}
    </div>
  );
}
